import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, Loader } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  isLoading: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onClear,
  isLoading
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className="relative" data-test="global-search-input-container">
      <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
        <IconContainer size="5" color="secondary">
          <Search />
        </IconContainer>
      </div>

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("globalSearch.searchInput.placeholder")}
        className="w-full h-12 pl-12 text-xl bg-transparent border-none focus:outline-none placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white"
        role="searchbox"
        aria-expanded={false}
        autoComplete="off"
        spellCheck={false}
        data-test="global-search-input"
      />

      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
        {isLoading && <Loader />}

        {value && !isLoading && (
          <div className="flex flex-row items-center gap-1 ml-4">
            <Button
              onClick={onClear}
              variant="tertiary"
              data-test="global-search-clear-button"
            >
              {t("globalSearch.searchInput.clear")}
            </Button>
            <Text variant="bodyTertiary">|</Text>
          </div>
        )}
      </div>
    </div>
  );
};
